import React, { memo, useState } from "react";
import { FieldArray } from "formik";
import { Bin, CardBin } from "../../../store/card/types";
import { Grid, Box, Button, Typography } from "@mui/material";
import pageStyles from "../../pageStyles";
import Confirm from "../../../common/confirm/Confirm";
import { ImageAspectRatio, Add } from "@mui/icons-material/";
import CardBinFilter from "./CardBinFilter";
import BinItem from "./BinItem";
import AddEditBinDialog from "./AddEditBinDialog";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";

interface BinForm {
  cardBinIndex: number;
  cardBin?: CardBin;
}

export interface FilterBin {
  from: string;
  to: string;
  length: string;
  accounts: string[];
  keys: string[];
  floorLimit: string[];
}

export type AddEditBinParams = {
  isOpen: boolean;
  isAddNew?: boolean;
  cardBinIndex?: number;
  binIndex?: number;
  binData?: Bin;
};

const classes = {
  binRoot: {
    width: "100%",
    border: "1px solid #efefef",
    borderRadius: 1,
    "& :hover": {
      background: "rgba(250, 250, 250, 0.5)",
    },
    "& *": {
      boxSizing: "border-box",
    },
  },
  binItem: {
    padding: 1,
    display: "flex",
    borderTop: "1px solid #efefef",
    alignItems: "center",

    "& *": {
      boxSizing: "border-box",
    },
  },
  binTitle: {
    background: "#fafafa",
    padding: 1,
    fontSize: 13,
    display: "flex",
  },
  binValuesCol: {
    width: 100,
    marginRight: 2,
  },
  binAccountsCol: {
    flex: 1,
    marginLeft: 1,
  },
  binActionCol: {
    width: 100,
    textAlign: "center",
  },
};

export const accounts = ["Cheque", "Savings", "Credit", "Default"];

const BinForm = ({ cardBin, cardBinIndex }: BinForm) => {
  const styles = pageStyles();
  const allcardtypes = useSelector(
    (state: ApplicationState) => state.cardType.data?.data || []
  );

  const [showConfirm, setShowConfirm] = useState(false);
  const [binEditAddDialogData, setBinEditAddDialogData] = useState<
    AddEditBinParams
  >({
    isOpen: false,
    isAddNew: false,
  });
  const [deleteItemId, setDeleteItemId] = useState(0);
  const [filteredBin, setFilteredBin] = useState<Bin[]>(cardBin?.Bin || []);
  const [filter, setFilter] = useState<FilterBin>({
    from: "",
    to: "",
    length: "",
    accounts: [],
    keys: [],
    floorLimit: [],
  });

  const handleSubmitFilter = () => {
    const { from, to, length, accounts, keys, floorLimit } = filter;
    let filteredData = cardBin?.Bin || [];

    if (from) {
      filteredData = filteredData.filter((bin) =>
        bin.From.toLowerCase().startsWith(from.toLowerCase())
      );
    }
    if (to) {
      filteredData = filteredData.filter((bin) =>
        bin.To.toLowerCase().startsWith(to.toLowerCase())
      );
    }
    if (length) {
      filteredData = filteredData.filter(
        (bin) => bin.Length === parseInt(length)
      );
    }
    if (accounts.length) {
      filteredData = filteredData.filter((bin) =>
        bin.Account.some((account) => accounts.includes(account))
      );
    }

    setFilteredBin(filteredData);
  };

  const handleAddNewBin = (cardBinIndex: number) => {
    setBinEditAddDialogData({
      isOpen: true,
      isAddNew: true,
      cardBinIndex,
      binIndex: filteredBin.length,
    });
  };

  return (
    <FieldArray
      name={`attributes.CardBin[${cardBinIndex}].Bin`}
      render={(arrayHelpers) => {
        return (
          <>
            <Box>
              <Box className={styles.processorTitleWrapper} marginBottom={2}>
                <Box display="flex" alignItems="center">
                  <Typography className={styles.processorTitle}>
                    <ImageAspectRatio style={{ marginRight: "5px" }} />
                    Bin List
                  </Typography>
                  <Box marginLeft={1}>
                    ({filteredBin.length}{" "}
                    {filteredBin.length > 1 ? "items" : "item"})
                  </Box>
                </Box>
                <Box>
                  <CardBinFilter
                    filter={filter}
                    setFilter={setFilter}
                    handleSubmit={handleSubmitFilter}
                  />
                </Box>
              </Box>

              <Box sx={classes.binRoot}>
                <Box sx={classes.binTitle}>
                  <Box sx={classes.binValuesCol}>From</Box>
                  <Box sx={classes.binValuesCol}>To</Box>
                  <Box sx={classes.binValuesCol}>Length</Box>
                  <Box sx={classes.binAccountsCol}>Accounts</Box>
                  <Box sx={classes.binActionCol}>Action</Box>
                </Box>
                {filteredBin.length === 0 ? (
                  <Box sx={classes.binItem} textAlign="center">
                    <Typography style={{ width: "100%" }}>
                      No Bin found
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {filteredBin?.map((bin: Bin, binIndex) => (
                      <BinItem
                        key={binIndex}
                        bin={bin}
                        binIndex={binIndex}
                        cardBinIndex={cardBinIndex}
                        setDeleteItemId={setDeleteItemId}
                        setShowConfirm={setShowConfirm}
                        setBinEditAddDialogData={setBinEditAddDialogData}
                      />
                    ))}
                  </>
                )}

                <Confirm
                  isOpen={showConfirm}
                  onCancel={() => setShowConfirm(false)}
                  onConfirm={() => {
                    setFilteredBin(
                      filteredBin.filter((item, i) => i !== deleteItemId)
                    );
                    arrayHelpers.remove(deleteItemId);
                    setShowConfirm(false);
                  }}
                  title="Delete Bin"
                  icon="danger"
                  confirmLabel="Delete"
                  contentText={`You are about to delete a Bin`}
                />
              </Box>
            </Box>

            <Grid item xs={12}>
              <Box textAlign="center" marginTop={4}>
                <Button
                  color="primary"
                  variant="outlined"
                  size="medium"
                  endIcon={<Add />}
                  onClick={() => {
                    arrayHelpers.push({
                      Account: [],
                      From: "",
                      To: "",
                      Length: 0,
                    });
                    handleAddNewBin(cardBinIndex);
                  }}
                >
                  Create Bin
                </Button>

                {binEditAddDialogData.isOpen ? (
                  <AddEditBinDialog
                    cardTypes={allcardtypes}
                    binEditAddDialogData={binEditAddDialogData}
                    setBinEditAddDialogData={setBinEditAddDialogData}
                    setFilteredBin={setFilteredBin}
                    filteredBin={filteredBin}
                  />
                ) : null}
              </Box>
            </Grid>
          </>
        );
      }}
    />
  );
};

export default memo(BinForm);
